/*
 * Queues data
 *
 * - Holds the current set of queues
 * - Handles everything about queues from the dashboard
 */

import { computed, ref, reactive } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useApiStore } from '@/stores/Api'
import { usePhonebookStore } from '@/stores/Phonebook'

import logger from '@/utilities/Logger'
import globalEvents from '@/utilities/GlobalEvents'
import _i18n from '@/utilities/I18n'
import { sleep } from '@/utilities/Tools'

import type { PbxAppQueuesAllItem, PbxAppQueuesAllResponse, PbxAppQueuesCaller, PbxAppQueuesCallerState } from '@/TcxApi/PbxApp'
import type { ConfigEventCommon, PbxEventQueueEntered, PbxEventQueueLeft, PbxEventQueueMemberJoin, PbxEventQueueMemberLeft, PbxEventQueueMemberUpdate } from '@per_moeller/tcx-realtime-client'
import type { AxiosError } from 'axios'

export const useQueuesStore = defineStore('queues', () => {
	logger.debug('Queues: Initializing store')

	const i18n = _i18n.global
	const api = useApiStore()
	const realtime = useRealtimeStore()
	const ss = useSettingsStore()
	const phonebook = usePhonebookStore()

	// #region Data

	const queues = ref<PbxAppQueuesAllItem[]>([])
	const _ready = ref(false)

	// #endregion

	// #region Initializing, loading data

	realtime.configEvents.on('PBX_QUEUE_CREATED', queueCreatedEventHandler)
	realtime.configEvents.on('PBX_QUEUE_UPDATED', queueUpdatedEventHandler)
	realtime.configEvents.on('PBX_QUEUE_DELETED', queueDeletedEventHandler)
	realtime.pbxEvents.on('QUEUE_ENTERED', callerEnteredHandler)
	realtime.pbxEvents.on('QUEUE_LEFT', callerLeftHandler)
	realtime.pbxEvents.on('QUEUE_MEMBER_JOIN', memberJoin)
	realtime.pbxEvents.on('QUEUE_MEMBER_UPDATE', memberUpdate)
	realtime.pbxEvents.on('QUEUE_MEMBER_LEFT', memberLeft)
	realtime.realtimeEvents.on('Connected', init)
	globalEvents.on('Auth:LoggedOut', () => { queues.value = [] })
	globalEvents.on('StatusRequest', () => {
		globalEvents.emit('StatusReport', { key: 'queues', value: _ready.value })
	})

	if (realtime.connected) { init() } // else we do it on Realtime:Connected

	globalEvents.on('Midnight', () => { midnightRefresh() })

	function midnightRefresh() {
		logger.info('Queues: Performing midnight refresh')
		init()
	}

	async function init() {
		logger.info('Queues: Loading')
		_ready.value = false

		try {
			await ss.ready()

			const res = await api.get<PbxAppQueuesAllResponse>('/pbx/app/queues?all=true')

			// Process dates in incoming data
			for (const queue of res.data) {
				for (const member of queue.members) {
					if (member.lastCallEnded) {
						member.lastCallEnded = new Date(member.lastCallEnded)
					}
				}

				for (const caller of queue.callers) {
					if (caller.entered) {
						caller.entered = new Date(caller.entered)
					}
					if (caller.answered) {
						caller.answered = new Date(caller.answered)
					}
				}
			}

			// Build set of new queue IDs for quick lookup
			const newQueueIds = new Set(res.data.map(queue => queue._id))

			// Remove queues that no longer exist (iterate backwards to safely splice)
			for (let idx = queues.value.length - 1; idx >= 0; idx--) {
				const queue = queues.value[idx]
				if (queue && !newQueueIds.has(queue._id)) {
					queues.value.splice(idx, 1)
				}
			}

			// Update existing queues or add new ones
			for (const newQueue of res.data) {
				const existingQueue = queues.value.find(queue => queue._id === newQueue._id)

				if (existingQueue) {
					// Update existing queue in place to preserve reactivity
					existingQueue.name = newQueue.name
					existingQueue.number = newQueue.number
					existingQueue.dynamic = newQueue.dynamic
					existingQueue.member = newQueue.member
					existingQueue.serviceLevel = newQueue.serviceLevel
					existingQueue.serviceLevelCritical = newQueue.serviceLevelCritical
					existingQueue.priority = newQueue.priority

					// Update members array in place
					existingQueue.members.splice(0, existingQueue.members.length, ...newQueue.members.map(member => reactive(member)))

					// Update callers array in place
					existingQueue.callers.splice(0, existingQueue.callers.length, ...newQueue.callers.map(caller => reactive(caller)))
				} else {
					// Add new queue
					queues.value.push(reactive(newQueue))
				}
			}

			_ready.value = true
			globalEvents.emit('Queues:Loaded')
			logger.info(`Queues: ${queues.value.length} queues loaded`)
		}
		catch (_) { /** Ignore */ }
	}

	// #endregion

	// #region Event handling

	async function queueCreatedEventHandler(event: ConfigEventCommon) {
		try {
			const res = await api.get<PbxAppQueuesAllResponse>(`/pbx/app/queues?all=true&queue=${event.id}`)

			if (res.data.length === 0) { return } // User does not have access to this queue, ignore

			queues.value.push(reactive(res.data[0] as PbxAppQueuesAllItem))
			globalEvents.emit('Queues:Created', res.data[0] as PbxAppQueuesAllItem)
		}
		catch (err) {
			if ((err as AxiosError).status !== 0 && (err as AxiosError).code !== 'network') {
				globalEvents.emit('CommonErrorModal', {
					header: i18n.t('queues.errorHeader'),
					message: i18n.t('queues.errorLoadingNewlyCreatedQueueMessage').replace('%%id%%', event.id).replace('%%response%%', (err as AxiosError).message)
				})
			}
		}
	}

	async function queueUpdatedEventHandler(event: ConfigEventCommon) {
		try {
			const res = await api.get<PbxAppQueuesAllResponse>(`/pbx/app/queues?all=true&queue=${event.id}`)
			if (res.data.length === 0) {
				// User does not have access to this queue anymore, remove it
				const index = queues.value.findIndex(queue => queue._id === event.id)
				if (index !== -1) {
					const queue = queues.value[index] as PbxAppQueuesAllItem

					queues.value.splice(index, 1)
					globalEvents.emit('Queues:Deleted', queue)
				}
			} else {
				// Got a queue from api
				const index = queues.value.findIndex(queue => queue._id == event.id)
				if (index !== -1) {
					// And we have the queue in our list, update it
					queues.value[index] = reactive(res.data[0] as PbxAppQueuesAllItem)
					globalEvents.emit('Queues:Updated', res.data[0] as PbxAppQueuesAllItem)
				} else {
					// We don't have the queue in our list, add it
					queues.value.push(reactive(res.data[0] as PbxAppQueuesAllItem))
					globalEvents.emit('Queues:Created', res.data[0] as PbxAppQueuesAllItem)
				}
			}
		}
		catch (err) {
			if ((err as AxiosError).status !== 0 && (err as AxiosError).code !== 'network') {
				globalEvents.emit('CommonErrorModal', {
					header: i18n.t('queues.errorHeader'),
					message: i18n.t('queues.errorLoadingUpdatedQueueMessage').replace('%%id%%', event.id).replace('%%response%%', (err as AxiosError).message)
				})
			}
		}
	}

	function queueDeletedEventHandler(event: ConfigEventCommon) {
		const index = queues.value.findIndex(queue => queue._id === event.id)
		if (index !== -1) {
			const queue = queues.value[index] as PbxAppQueuesAllItem

			queues.value.splice(index, 1)
			globalEvents.emit('Queues:Deleted', queue)
		}
	}

	function callerEnteredHandler(event: PbxEventQueueEntered) {
		event.entered = new Date(event.entered)
		const queueId = event.queue

		const index = queues.value.findIndex(queue => queue._id === queueId)

		if (index !== -1) {
			if (event.number === '' || event.number?.toLowerCase() === 'anonymous') { event.privacy = true } // Extra check for privacy
			if (event.name?.toLowerCase() === 'anonymous') { event.privacy = true } // Extra check for privacy

			const name = phonebook.findNameFromNumber(event.number)

			if (name) { event.name = name }

			const newCall: PbxAppQueuesCaller = reactive({
				_id: event._id,
				state: event.state as PbxAppQueuesCallerState,
				number: event.number,
				name: event.name ?? '',
				privacy: event.privacy,
				entered: event.entered,
				sessionId: event.sessionId
			})

			queues.value[index]!.callers.push(newCall) // eslint-disable-line @typescript-eslint/no-non-null-assertion
			logger.debug(`Queues: Caller ${event.number} ${event.name} entered queue ${queues.value[index]?.name}, now has ${queues.value[index]?.callers.length} callers`)
			globalEvents.emit('Queues:Entered', { queue: queueId, caller: newCall })
		}
	}

	function callerLeftHandler(event: PbxEventQueueLeft) {
		event.entered = new Date(event.entered)
		if (event.answered) { event.answered = new Date(event.answered) }
		const queueId = event.queue

		const index = queues.value.findIndex(queue => queue._id === queueId)

		if (index !== -1) {
			const callerIndex = queues.value[index]?.callers.findIndex(caller => caller._id === event._id) as number

			if (callerIndex !== -1) {
				const call = queues.value[index]?.callers[callerIndex] as PbxAppQueuesCaller

				queues.value[index]?.callers.splice(callerIndex, 1)
				call.state = event.state as PbxAppQueuesCallerState
				call.answered = event.answered
				call.answeredByExtension = event.answeredByExtension
				call.answeredByNumber = event.answeredByNumber
				call.sessionId = event.sessionId

				logger.debug(`Queues: Caller ${event.number} - ${event.name} left queue ${queues.value[index]?.name}, now has ${queues.value[index]?.callers.length} callers`)
				globalEvents.emit('Queues:Left', { queue: queueId, caller: call, index: callerIndex })
			}
		}
	}

	function memberJoin(event: PbxEventQueueMemberJoin) {
		if (event.data.lastCallEnded) { event.data.lastCallEnded = new Date(event.data.lastCallEnded) }
		const index = queues.value.findIndex(queue => queue._id === event._id)

		if (index !== -1) {
			queues.value[index]?.members.push(reactive(event.data))
			globalEvents.emit('Queues:MemberJoin', { queue: event._id, member: event.data })
			logger.debug(`Queues: Member ${event.data._id} ${event.data.number || ''} joined queue ${queues.value[index]?.name}`)
		}
	}

	function memberUpdate(event: PbxEventQueueMemberUpdate) {
		if (event.data.lastCallEnded) { event.data.lastCallEnded = new Date(event.data.lastCallEnded) }
		const index = queues.value.findIndex(queue => queue._id === event._id)

		if (index != -1) {
			const memberIndex = queues.value[index]?.members.findIndex(member => member._id === event.data._id) as number

			if (memberIndex !== -1 && queues.value[index]) {
				queues.value[index].members[memberIndex] = reactive(event.data)
				globalEvents.emit('Queues:MemberUpdate', { queue: event._id, member: event.data, index: memberIndex })
				logger.debug(`Queues: Member ${event.data._id} ${event.data.number || ''} updated in queue ${queues.value[index].name}`)
			}
		}
	}

	function memberLeft(event: PbxEventQueueMemberLeft) {
		if (event.data.lastCallEnded) { event.data.lastCallEnded = new Date(event.data.lastCallEnded) }
		const index = queues.value.findIndex(queue => queue._id === event._id)

		if (index !== -1) {
			const memberIndex = queues.value[index]?.members.findIndex(member => member._id === event.data._id) as number

			if (memberIndex != -1 && queues.value[index]) {
				queues.value[index].members.splice(memberIndex, 1)
				globalEvents.emit('Queues:MemberLeft', { queue: event._id, member: event.data, index: memberIndex })
				logger.debug(`Queues: Member ${event.data._id} ${event.data.number || ''} left queue ${queues.value[index].name}`)
			}
		}
	}

	// #endregion

	// #region public APIs

	/** Wait for queues to be ready */
	async function ready() {
		if (_ready.value) { return }

		while (!_ready.value) { await sleep(250) }
	}

	/** Is queues ready */
	const isReady = computed(() => { return _ready.value })

	/** Get list of queues for list usage */
	const list = computed(() => {
		return queues.value.map(queue => {
			return { value: queue._id, name: queue.name, label: queue.name + (queue.number ? ` (${queue.number})` : '') }
		})
	})

	/**
	 * Get a queue by id
	 * @param id Queue id
	 */
	function getQueue(id: string) {
		return queues.value.find(queue => queue._id === id)
	}

	// #endregion

	return { isReady, ready, queues, list, getQueue }
})
